import React from 'react';
import { Calculator, Rocket, FileText, ChevronUp, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { TooltipButton } from './TooltipButton';

export interface BottomModeToolbarProps {
  activeMode: 'draw' | 'estimate' | 'specs' | 'neca' | 'rfi';
  showModeDropdown: boolean;
  setShowModeDropdown: (v: boolean | ((p: boolean) => boolean)) => void;
  modeDropdownRef: React.RefObject<HTMLDivElement | null>;
  onModeChange?: (mode: 'draw' | 'estimate' | 'specs') => void;
  // Specs mode
  specsFiles?: Array<{ id: string; file_name?: string; name?: string }>;
  currentSpecsFile?: { id: string; file_name?: string; name?: string } | null;
  onSpecsFileChange?: (id: string) => void;
  specsPageNumber: number;
  specsNumPages: number | null;
  specsScale: number;
  setSpecsPageNumber: (v: number | ((p: number) => number)) => void;
  setSpecsScale: (v: number | ((p: number) => number)) => void;
  rotateSpecsPage: () => void;
  // NECA mode
  necaPageNumber: number;
  necaNumPages: number | null;
  necaScale: number;
  setNecaPageNumber: (v: number | ((p: number) => number)) => void;
  setNecaScale: (v: number | ((p: number) => number)) => void;
  // Draw mode
  pageNumber: number;
  numPages: number | null;
  pageInputValue: string;
  setPageInputValue: (v: string) => void;
  setPageNumber: (v: number | ((p: number) => number)) => void;
  scale: number;
  setScale: (v: number | ((p: number) => number)) => void;
  zoomOutFromToolbar: () => void;
  zoomInFromToolbar: () => void;
  rotateCurrentPage: () => void;
  activeRenderTokenRef: React.MutableRefObject<number>;
  shouldCenterAfterRenderRef: React.MutableRefObject<boolean>;
  defaultViewScale: number;
}

export const BottomModeToolbar: React.FC<BottomModeToolbarProps> = ({
  activeMode,
  showModeDropdown,
  setShowModeDropdown,
  modeDropdownRef,
  onModeChange,
  specsFiles,
  currentSpecsFile,
  onSpecsFileChange,
  specsPageNumber,
  specsNumPages,
  specsScale,
  setSpecsPageNumber,
  setSpecsScale,
  rotateSpecsPage,
  necaPageNumber,
  necaNumPages,
  necaScale,
  setNecaPageNumber,
  setNecaScale,
  pageNumber,
  numPages,
  pageInputValue,
  setPageInputValue,
  setPageNumber,
  scale,
  setScale,
  zoomOutFromToolbar,
  zoomInFromToolbar,
  rotateCurrentPage,
  activeRenderTokenRef,
  shouldCenterAfterRenderRef,
  defaultViewScale,
}) => {
  return (
    <div className={`pdf-annotator-toolbar ${activeMode === 'draw' ? 'draw-mode' : ''}`}>
      {/* Mode Selector Dropup */}
      <div ref={modeDropdownRef} style={{ position: 'relative' }}>
        <button
          className={`toolbar-btn mode-selector-btn${showModeDropdown ? ' active' : ''}`}
          onClick={() => setShowModeDropdown((prev) => !prev)}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {activeMode === 'estimate' ? (
            <Calculator size={16} />
          ) : activeMode === 'specs' ? (
            <FileText size={16} />
          ) : (
            <Rocket size={16} />
          )}
          <ChevronUp size={12} className={`mode-chevron${showModeDropdown ? ' open' : ''}`} />
        </button>
        {showModeDropdown && (
          <div className="mode-dropup">
            {[
              { mode: 'draw' as const, icon: <Rocket size={15} />, label: 'Takeoff' },
              // { mode: 'specs' as const, icon: <FileText size={15} />, label: 'Specs' },
            ].map(({ mode, icon, label }) => (
              <button
                key={mode}
                className={`mode-dropup-item${activeMode === mode ? ' active' : ''}`}
                onClick={() => {
                  onModeChange?.(mode);
                  setShowModeDropdown(false);
                }}
              >
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {activeMode === 'specs' && (
        <>
          {specsFiles && specsFiles.length > 1 && (
            <select
              value={currentSpecsFile?.id || ''}
              onChange={(e) => onSpecsFileChange?.(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-primary)',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {specsFiles.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.file_name || file.name}
                </option>
              ))}
            </select>
          )}
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={specsPageNumber <= 1}
            tooltip="Previous Page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <span className="page-info">
            Page {specsPageNumber} of {specsNumPages || '...'}
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsPageNumber((prev) => Math.min(prev + 1, specsNumPages || 1))}
            disabled={specsPageNumber >= (specsNumPages || 1)}
            tooltip="Next Page"
          >
            <ChevronRight size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsScale((prev) => Math.max(prev - 0.2, 0.5))}
            tooltip="Zoom Out"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(specsScale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setSpecsScale((prev) => Math.min(prev + 0.2, 3.0))}
            tooltip="Zoom In"
          >
            <ZoomIn size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => rotateSpecsPage()}
            tooltip="Rotate Page 90°"
          >
            <RotateCw size={16} />
          </TooltipButton>
        </>
      )}

      {activeMode === 'neca' && (
        <>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={necaPageNumber <= 1}
            tooltip="Previous Page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <span className="page-info">
            Page {necaPageNumber} of {necaNumPages || '...'}
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaPageNumber((prev) => Math.min(prev + 1, necaNumPages || 1))}
            disabled={necaPageNumber >= (necaNumPages || 1)}
            tooltip="Next Page"
          >
            <ChevronRight size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaScale((prev) => Math.max(prev - 0.2, 0.5))}
            tooltip="Zoom Out"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(necaScale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => setNecaScale((prev) => Math.min(prev + 0.2, 3.0))}
            tooltip="Zoom In"
          >
            <ZoomIn size={16} />
          </TooltipButton>
        </>
      )}

      {activeMode === 'draw' && (
        <>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => {
              activeRenderTokenRef.current += 1;
              shouldCenterAfterRenderRef.current = true;
              setPageNumber((prev) => Math.max(prev - 1, 1));
              setScale(defaultViewScale);
            }}
            disabled={pageNumber <= 1}
            tooltip="Previous Page"
          >
            <ChevronLeft size={16} />
          </TooltipButton>
          <div className="page-number-container">
            <input
              type="text"
              className="page-number-input"
              value={pageInputValue}
              onChange={(e) => {
                const value = e.target.value;
                setPageInputValue(value);
                const newPage = parseInt(value);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= (numPages || 1)) {
                  activeRenderTokenRef.current += 1;
                  shouldCenterAfterRenderRef.current = true;
                  setPageNumber(newPage);
                  setScale(defaultViewScale);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              onBlur={() => {
                setPageInputValue(pageNumber.toString());
              }}
            />
            <span className="page-total">of {numPages || '...'}</span>
          </div>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => {
              activeRenderTokenRef.current += 1;
              shouldCenterAfterRenderRef.current = true;
              setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
              setScale(defaultViewScale);
            }}
            disabled={!numPages || pageNumber >= numPages}
            tooltip="Next Page"
          >
            <ChevronRight size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={zoomOutFromToolbar}
            tooltip="Zoom Out (Ctrl/Cmd + -)"
          >
            <ZoomOut size={16} />
          </TooltipButton>
          <span className="page-info" style={{ minWidth: '60px' }}>
            {Math.round(scale * 100)}%
          </span>
          <TooltipButton
            className="toolbar-btn"
            onClick={zoomInFromToolbar}
            tooltip="Zoom In (Ctrl/Cmd + +)"
          >
            <ZoomIn size={16} />
          </TooltipButton>
          <TooltipButton
            className="toolbar-btn"
            onClick={() => rotateCurrentPage()}
            tooltip="Rotate Page 90°"
          >
            <RotateCw size={16} />
          </TooltipButton>
        </>
      )}
    </div>
  );
};
